import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./question.css";
import { API_URL } from "./constants";

interface Question {
  id: number;
  question_text: string;
  question_type: string;
  options: string[] | null;
  category: string;
  branch_id: number;
}

interface Branch {
  id: number;
  branch_name: string;
  branch_order: number;
  description: string;
}

interface Progress {
  answered: number;
  estimated_total: number;
  percentage: number;
}

const Assessment: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>("");
  const [branch, setBranch] = useState<Branch | null>(null);
  const [progress, setProgress] = useState<Progress>({ answered: 0, estimated_total: 23, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (assessmentId) {
      fetchBranch();
    }
  }, [assessmentId]);

  const fetchBranch = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/assessment/${assessmentId}/branch`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setBranch(data.branch);
        setQuestions(data.questions);
        setProgress(data.progress);
        setCurrentQuestionIndex(0);
        setSelectedAnswer("");
        
        // If branch complete, auto-advance
        if (data.branch_complete) {
          await handleNextBranch();
        }
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to fetch questions");
      }
    } catch (err) {
      console.error("Failed to fetch branch:", err);
      setError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer) {
      setError("Please select an answer");
      return;
    }

    setSubmitting(true);
    setError("");

    const token = localStorage.getItem("token");
    const currentQuestion = questions[currentQuestionIndex];

    try {
      const res = await fetch(`${API_URL}/api/assessment/${assessmentId}/answers`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          question_id: currentQuestion.id,
          answer_text: selectedAnswer
        })
      });

      if (res.ok) {
        setProgress(prev => ({
          ...prev,
          answered: prev.answered + 1,
          percentage: Math.min(((prev.answered + 1) / prev.estimated_total) * 100, 100)
        }));

        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setSelectedAnswer("");
        } else {
          await handleNextBranch();
        }
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to submit answer");
      }
    } catch (err) {
      console.error("Failed to submit answer:", err);
      setError("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextBranch = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/assessment/${assessmentId}/branch/next`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.complete) {
          await completeAssessment();
        } else {
          setBranch(data.branch);
          setQuestions(data.questions);
          setProgress(data.progress);
          setCurrentQuestionIndex(0);
          setSelectedAnswer("");
        }
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to advance");
      }
    } catch (err) {
      console.error("Failed to get next branch:", err);
      setError("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const completeAssessment = async () => {
    const token = localStorage.getItem("token");
    
    try {
      const res = await fetch(`${API_URL}/api/assessment/${assessmentId}/complete`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.ok) {
        const data = await res.json();
        alert(`🎉 Assessment Complete!\n\nYour Cyber Risk Score: ${data.score}/100\nRisk Level: ${data.tier.replace('_', ' ').toUpperCase()}`);
        navigate("/dashboard");
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to complete assessment");
      }
    } catch (err) {
      console.error("Failed to complete assessment:", err);
      setError("An error occurred");
    }
  };

  if (loading) {
    return (
      <div className="question-container">
        <div className="question-form">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loader" style={{ margin: '0 auto 1rem' }}></div>
            <p>Loading questions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="question-container">
        <div className="question-form">
          <h2>Loading Next Section...</h2>
          <p>Please wait while we prepare your questions.</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="question-container">
      <div className="question-form">
        {/* Branch Header */}
        <div style={{ 
          marginBottom: '1.5rem', 
          padding: '1rem', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          borderRadius: '12px',
          color: 'white'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.25rem' }}>{branch?.branch_name}</h3>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', opacity: 0.9 }}>{branch?.description}</p>
        </div>

        {/* Progress Bar */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
            <span>Question {currentQuestionIndex + 1} of {questions.length}</span>
            <span>{Math.round(progress.percentage)}% Complete</span>
          </div>
          <div style={{ background: '#e0e0e0', borderRadius: '10px', height: '10px', overflow: 'hidden' }}>
            <div 
              style={{ 
                background: 'linear-gradient(90deg, #667eea, #764ba2)', 
                height: '100%', 
                borderRadius: '10px',
                transition: 'width 0.3s ease',
                width: `${progress.percentage}%` 
              }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="question-block">
          <p style={{ color: '#667eea', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            {currentQuestion.category}
          </p>
          <p className="question-text" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
            {currentQuestion.question_text}
          </p>

          <div className="options-group" style={{ marginTop: '1.5rem' }}>
            {currentQuestion.question_type === "yes_no" ? (
              <>
                {["Yes", "No"].map((option) => (
                  <label key={option} className="option" style={{ 
                    display: 'block', 
                    padding: '1rem', 
                    marginBottom: '0.75rem', 
                    border: selectedAnswer === option ? '2px solid #667eea' : '2px solid #e0e0e0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: selectedAnswer === option ? '#f0f4ff' : 'white',
                    transition: 'all 0.2s'
                  }}>
                    <input
                      type="radio"
                      name="answer"
                      value={option}
                      checked={selectedAnswer === option}
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                      style={{ marginRight: '0.75rem' }}
                    />
                    {option}
                  </label>
                ))}
              </>
            ) : currentQuestion.options ? (
              currentQuestion.options.map((option, index) => (
                <label key={index} className="option" style={{ 
                  display: 'block', 
                  padding: '1rem', 
                  marginBottom: '0.75rem', 
                  border: selectedAnswer === option ? '2px solid #667eea' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: selectedAnswer === option ? '#f0f4ff' : 'white',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="answer"
                    value={option}
                    checked={selectedAnswer === option}
                    onChange={(e) => setSelectedAnswer(e.target.value)}
                    style={{ marginRight: '0.75rem' }}
                  />
                  {option}
                </label>
              ))
            ) : (
              <input
                type="text"
                value={selectedAnswer}
                onChange={(e) => setSelectedAnswer(e.target.value)}
                placeholder="Enter your answer"
                style={{ 
                  width: '100%', 
                  padding: '1rem', 
                  border: '2px solid #e0e0e0', 
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            )}
          </div>
        </div>

        {error && <p style={{ color: '#e53e3e', marginTop: '1rem' }}>{error}</p>}

        {/* Submit Button */}
        <button 
          onClick={handleSubmitAnswer} 
          disabled={submitting || !selectedAnswer}
          style={{
            width: '100%',
            padding: '1rem',
            marginTop: '1.5rem',
            background: submitting || !selectedAnswer ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: submitting || !selectedAnswer ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s'
          }}
        >
          {submitting 
            ? "Saving..." 
            : currentQuestionIndex === questions.length - 1 
              ? "Next Section →" 
              : "Next Question →"}
        </button>
      </div>
    </div>
  );
};

export default Assessment;